import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent, Typography, Box, IconButton, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { globalNotifyError } from 'views/utilities/AlertToast';
import { useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const OtpVerificationDialog = ({ open, onClose, onSubmit, onResend, otpLength = 6, isResendDisabled = false, resending, verifying }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [otpValues, setOtpValues] = useState(Array(otpLength).fill(''));
    const [timer, setTimer] = useState(30);

    // Auto-focus first input on open
    useEffect(() => {
        if (open) {
            setOtpValues(Array(otpLength).fill(''));
            setTimer(30);
            setTimeout(() => {
                const firstInput = document.getElementById('otp-input-0');
                if (firstInput) firstInput.focus();
            }, 150);
        }
    }, [open, otpLength]);

    // Resend countdown timer
    useEffect(() => {
        let interval = null;
        if (open && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [open, timer]);

    const handleClose = () => {
        setOtpValues(Array(otpLength).fill(''));
        onClose();
    };

    const handleResend = () => {
        setOtpValues(Array(otpLength).fill(''));
        setTimer(30);
        onResend();
    };

    // Handle change inside individual digit inputs
    const handleChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const newOtp = [...otpValues];
        newOtp[index] = value.slice(-1); // Keep only the latest typed character
        setOtpValues(newOtp);

        // Auto-focus next input
        if (value && index < otpLength - 1) {
            const nextInput = document.getElementById(`otp-input-${index + 1}`);
            if (nextInput) nextInput.focus();
        }

        // Auto-submit if all inputs are filled
        const combinedOtp = newOtp.join('');
        if (combinedOtp.length === otpLength) {
            onSubmit(combinedOtp);
        }
    };

    // Handle backspace navigation
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (!otpValues[index] && index > 0) {
                const newOtp = [...otpValues];
                newOtp[index - 1] = '';
                setOtpValues(newOtp);
                const prevInput = document.getElementById(`otp-input-${index - 1}`);
                if (prevInput) {
                    prevInput.focus();
                }
            } else {
                const newOtp = [...otpValues];
                newOtp[index] = '';
                setOtpValues(newOtp);
            }
        }
    };

    // Handle paste event (e.g. paste a 6 digit code)
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();
        if (/^\d+$/.test(pastedData)) {
            const digits = pastedData.slice(0, otpLength).split('');
            const newOtp = [...otpValues];
            digits.forEach((digit, idx) => {
                if (idx < otpLength) {
                    newOtp[idx] = digit;
                }
            });
            setOtpValues(newOtp);

            // Focus on the last filled input
            const focusIndex = Math.min(digits.length, otpLength - 1);
            const inputElement = document.getElementById(`otp-input-${focusIndex}`);
            if (inputElement) {
                inputElement.focus();
            }

            const combinedOtp = newOtp.join('');
            if (combinedOtp.length === otpLength) {
                onSubmit(combinedOtp);
            }
        }
    };

    const handleSubmit = () => {
        const combinedOtp = otpValues.join('');
        if (combinedOtp.length === otpLength) {
            onSubmit(combinedOtp);
        } else {
            globalNotifyError(`Please enter a ${otpLength}-digit OTP.`);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth={isMobile}
            maxWidth={isMobile ? false : 'xs'}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : '16px',
                    padding: '24px 16px',
                    position: 'relative',
                    margin: isMobile ? 0 : 'auto',
                    width: isMobile ? '100%' : '420px',
                    maxWidth: '100%',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                }
            }}
        >
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    color: (theme) => theme.palette.grey[500],
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box
                    sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(33, 150, 243, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        mt: 1,
                        color: 'primary.main',
                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.1)'
                    }}
                >
                    <LockOutlinedIcon sx={{ fontSize: 28 }} />
                </Box>

                <Typography variant="h3" sx={{ fontWeight: 600, mb: 1, color: '#1e293b', textAlign: 'center' }}>
                    Verify OTP
                </Typography>

                <Typography variant="body2" color="textSecondary" align="center" sx={{ px: 2, mb: 2, lineHeight: 1.5 }}>
                    A security code has been sent to your registered mobile number. Please enter the verification code below.
                </Typography>

                {/* Individual OTP digit boxes */}
                <Box display="flex" justifyContent="center" gap={1.2} sx={{ my: 3, width: '100%' }}>
                    {otpValues.map((value, index) => (
                        <TextField
                            key={index}
                            id={`otp-input-${index}`}
                            value={value}
                            onChange={(e) => handleChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            onPaste={handlePaste}
                            inputProps={{
                                maxLength: 1,
                                style: {
                                    textAlign: 'center',
                                    fontSize: '1.4rem',
                                    fontWeight: '700',
                                    padding: '10px 0',
                                    color: '#1e293b'
                                }
                            }}
                            variant="outlined"
                            sx={{
                                width: isMobile ? '40px' : '46px',
                                height: isMobile ? '46px' : '52px',
                                '& .MuiOutlinedInput-root': {
                                    height: '100%',
                                    borderRadius: '10px',
                                    backgroundColor: '#f8fafc',
                                    '& fieldset': {
                                        borderColor: '#cbd5e1'
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#94a3b8'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'primary.main',
                                        borderWidth: '2px'
                                    }
                                }
                            }}
                        />
                    ))}
                </Box>

                {/* Resend OTP section */}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1, mb: 3 }}>
                    {timer > 0 ? (
                        <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            Didn't receive the OTP? Resend in <span style={{ fontWeight: 600, color: '#334155' }}>{timer}s</span>
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" color="textSecondary">
                                Didn't receive the OTP?
                            </Typography>
                            <LoadingButton
                                loading={resending}
                                onClick={handleResend}
                                disabled={isResendDisabled}
                                variant="text"
                                sx={{
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    p: 0,
                                    minWidth: 'auto',
                                    color: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        textDecoration: 'underline'
                                    }
                                }}
                            >
                                Resend OTP
                            </LoadingButton>
                        </Box>
                    )}
                </Box>

                {/* Submit Action Button */}
                <LoadingButton
                    loading={verifying}
                    variant="contained"
                    onClick={handleSubmit}
                    fullWidth
                    size="large"
                    sx={{
                        borderRadius: '10px',
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '0.95rem',
                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.2)',
                        '&:hover': {
                            boxShadow: '0 6px 16px rgba(33, 150, 243, 0.3)'
                        }
                    }}
                >
                    Verify & Proceed
                </LoadingButton>
            </DialogContent>
        </Dialog>
    );
};

OtpVerificationDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
    onResend: PropTypes.func.isRequired,
    otpLength: PropTypes.number,
    isResendDisabled: PropTypes.bool,
    resending: PropTypes.bool,
    verifying: PropTypes.bool
};

export default OtpVerificationDialog;
